import type { ReactNode } from 'react';
import { InterviewSetting } from '../contexts';
import { IEventEmitter } from '../events';
import { InterviewFactoryOptions } from '../factories';

export type FormLocation = 'form-embedded' | 'form-query-module' | 'polaris-app' | 'polaris-popup';
export interface IBaseQuestion {
  token: string;
  text: string;
  choice: string;
  media: string;
  options: IOption[];
  interview: string;
  media_alttext: string;
  interview_progress?: number | null;
  dxoc_is_alarming?: boolean;
  introText?: string;
}
export interface IQuestionWithIndex extends IBaseQuestion {
  index: number;
}
export type MiniInterviewType =
  | 'dxoc'
  | 'emergency'
  | 'symptoms'
  | 'treatment'
  | 'treatmentGuidance'
  | 'txoc'
  | 'pxoc';

export enum QuestionType {
  Duration = 'D',
  Profile = 'P',
  MultipleChoice = 'M',
  Cause = 'C',
  ArrowButton = 'A',
  Single = 'S',
  TextBox = 'T',
  Identifiers = 'ID',
  Insurance = 'I',
  Contact = 'CT',
  Medication = 'MED',
}
interface IOption {
  token: string;
  text: string;
  preselected: boolean | null;
  media: string;
  exclusive: boolean;
  media_alttext: string;
  free_text: boolean;
}

/** Question/Option types for Polaris Interviews */
export interface IPolarisInterviewOption
  // Extend original base option type
  extends Partial<IOption>,
    Pick<IInterviewOption, 'next' | 'only' | 'subText' | 'valid'> {}

export interface IPolarisInterviewQuestion
  // Extend original base question type
  extends Partial<Omit<IQuestionWithIndex, 'options'>>,
    Pick<IInterviewQuestion, 'configuration' | 'displayList' | 'id' | 'subText'> {
  index: number;
  options?: IPolarisInterviewOption[];
  text: string;
  answer?: any;
  media: string;
  media_alttext: string;
}

/** Props shared between all interviews */
export interface ICommonProps {
  /** Used for analytics tracking */
  formLocation: FormLocation;
  /** Type of interview to display */
  interviewType: MiniInterviewType;
  /** Callback when a new question or results load into the UI */
  onQuestionDisplay?: (questionIndex: number) => void;
  /** Callback when submit button is clicked on a question */
  onQuestionSubmit?: (questionIndex: number) => void;
  /**
   * Props specifically related to the Polaris UI.
   * If this object is passed, the embedded version of the interview
   * will display in an accordion.
   *
   * @example
   * // embedded interview displays without an accordion
   * polarisProps = undefined
   *
   * // embedded interview displays within an accordion
   * polarisProps = {
   *   displayPolarisUI: false
   *   titleQuestion: 'What is life?'
   * }
   *
   * // interview displays in a "Polaris"-style modal (mobile) or side drawer (desktop)
   * polarisProps = {
   *   displayPolarisUI: true,
   *   titleQuestion: 'Wut?'
   * }
   * */
  polarisProps?: {
    /** Whether the embedded accordion should automatically be expanded */
    defaultExpanded?: boolean;
    /** Whether the interview should display using the "Polaris" UI */
    displayPolarisUI: boolean;
    /** Central question being asked, e.g. "What's the best supplement for me?"  */
    titleQuestion?: ReactNode;
    /** Callback when Polaris full screen mode is exited */
    onExit?: () => void;
    /** Whether the interview should display progress bar */
    showProgressBar?: boolean;
  };

  slug: string;
  isEmbedded?: boolean;
  /** List of questions to display for any interview using Treatment Guidance */
  questions?: Array<IInterviewQuestion>;
  /** List of common causes associated with slug. */
  commonCauses?: Array<{ name: string; slug: string }>;
  complaints?: number[];
  /** Name of condition from content page. Used in intro text. Should be lower case. */
  conditionName?: string;
  /** Intro text to display at the top of the interview. */
  intro?: string;
}

/** Interview option type specific for Sanity based options */
export interface IInterviewOption {
  text: string;
  token: string;
  type: string;
  category?: string;
  /** Whether the option is of an other type aka 'exclusive' to match siteapi options. e.g. "None of the above" */
  exclusive?: boolean | null;
  /** Whether the option is an exception. Meaning, even if it is not marked as valid, continue with interview */
  exception?: boolean | null;
  /** Whether the option is valid for the question */
  valid?: boolean | null;
  /** Whether the option must be exclusively selected */
  only?: boolean | null;
  /** Outcome to be displayed for the option. e.g. email collection for txoc */
  outcome?: string | null;
  /** Next question to display after this option is selected */
  next?: IInterviewQuestion | null;
  subText?: string;
}
/** Interview option type specific for Sanity based questions */
export interface IInterviewQuestion {
  /** Question ID: comes from Sanity if part of unique Q&A. Otherwise would be undefined for hard-coded questions */
  id?: string;
  /** Question text */
  text: string;
  /** Type of question */
  choice: QuestionType;
  /** Question configuration set in Sanity */
  configuration?: {
    answerRequirements?: string | null;
    partner?: string | null;
    interview?: string | null;
    questionType?: string | null;
  };
  /** List of items to display in the question */
  displayList?: string[];
  /** Key used to identify question in interview i.e. uniqueQA */
  key?: string;
  /** Choices for questions */
  options?: IInterviewOption[];
  subText?: string;
  /** Question media */
  media: string;
  media_alttext: string;
}

/** Props shared between dynamic and treatment interviews */
export interface ISharedProps {}

/** Props related to the treatment interview */
export interface ITreatmentInterviewProps extends ISharedProps, ICommonProps {
  interviewType: 'treatment';
  conditionName: string;
  //costSlugs: string[];
  //predictors: number[];
}

/** AB-test props related to the dynamic interview */
export interface IDynamicInterviewABTestProps {}

/** Props related to the symptoms and emergency interviews */
export interface IDynamicInterviewProps
  extends ISharedProps,
    ICommonProps,
    IDynamicInterviewABTestProps {
  interviewType: 'symptoms' | 'emergency';
}

export interface IDiagnosisOfConcernInterviewProps extends ISharedProps, ICommonProps {
  interviewType: 'dxoc';
  /** Predictor IDs */
  dxocs: number[];
  /** Content page slug of complaint */
  slug: string;
}

export interface ITreatmentGuidanceProps extends ICommonProps {
  interviewType: 'treatmentGuidance';
  /** slug of url being queried. e.g. `/blog/supplements/best-atp-supplement` */
}

export interface ISupplierCardConfig {
  id: string;
  variant: string;
  header?: string;
  subheader?: string;
  description?: string;
  bulletPoints: {
    icon: string;
    text: string;
  }[];
  ctaButton: {
    label: string;
    url?: string;
  };
}
export interface ITxocProps extends ICommonProps {
  interviewType: 'txoc';
  outcomeCopy?: {
    header: string;
    explanation: string;
    outcome: string;
    color?: string;
  }[];
  supplierCtaCardConfigs?: ISupplierCardConfig[];
  treatment: {
    name: string;
    abbreviation?: string;
  };
}

export interface IPxocProps extends ICommonProps {
  interviewType: 'pxoc';
}

export interface IPolarisInterviewsProps {
  configs: IInterviewConfig[];
  eventEmitter?: IEventEmitter | null;
  initialInterview?: MiniInterviewType;
  interviewSettings?: InterviewSetting[];
  isOpen?: boolean;
  parentInterviewId?: string;
  setIsPopUpOpen?: (isOpen: boolean) => void;
}

export interface IInterviewConfig {
  interviewType: MiniInterviewType;
  context: Omit<InterviewFactoryOptions, 'interviewType'>;
  id: string;
}

export type IMiniInterviewProps =
  | IDiagnosisOfConcernInterviewProps
  | IDynamicInterviewProps
  | ITreatmentGuidanceProps
  | ITreatmentInterviewProps
  | ITxocProps
  | IPxocProps;

export type TreatmentSupplierIcon =
  | 'checkmark'
  | 'clock'
  | 'cost'
  | 'lock'
  | 'noVideo'
  | 'number'
  | 'rxBottle'
  | 'star'
  | 'video'
  | 'teleMedicine';

export type TreatmentSupplier = {
  bullet_points?: {
    icon: TreatmentSupplierIcon;
    text: string;
  }[];
  cta_button_label: string;
  header: string;
  modalUrl?: string;
  modal_header_intro?: string;
  referring_page_url: string;
  relevance: number;
  subheader: ReactNode;
  url?: string;
};
